// components/AppLayout.tsx — chrome for the authenticated app (spec §4.5 app group).
import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Wordmark } from './Wordmark';
import { Mark } from './Mark';
import { ThemeToggle } from './ThemeToggle';
import { CommandPalette, openCommandPalette } from './CommandPalette';
import { useAuth } from '../hooks/useAuth';
import {
  ChatIcon,
  CloseIcon,
  DocsIcon,
  GitIcon,
  KeyIcon,
  LogoutIcon,
  MenuIcon,
  NotesIcon,
  SearchIcon,
  SettingsIcon,
  VoiceIcon,
} from './icons';

const TABS = [
  { to: '/app', label: 'Chat', end: true, icon: ChatIcon },
  { to: '/app/upload', label: 'Docs', icon: DocsIcon },
  { to: '/app/notes', label: 'Notes', icon: NotesIcon },
  { to: '/app/git', label: 'Git', icon: GitIcon },
  { to: '/app/voice', label: 'Voice', icon: VoiceIcon },
  { to: '/app/agents', label: 'Agents', icon: KeyIcon },
  { to: '/app/settings', label: 'Settings', icon: SettingsIcon },
];

function pageTitle(pathname: string): string {
  if (pathname.startsWith('/app/setup')) return 'Setup';
  const match = [...TABS]
    .sort((a, b) => b.to.length - a.to.length)
    .find((t) => (t.end ? pathname === t.to : pathname.startsWith(t.to)));
  return match?.label ?? 'Chat';
}

export function AppLayout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const title = pageTitle(location.pathname);

  const nav = (
    <nav className="flex flex-1 flex-col gap-1 px-3" aria-label="App">
      {TABS.map((t) => {
        const Icon = t.icon;
        return (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            onClick={() => setDrawerOpen(false)}
            className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}
          >
            <Icon className="h-[18px] w-[18px]" />
            {t.label}
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div className="flex min-h-full">
      {/* Sidebar — static on lg+, off-canvas drawer on mobile */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-line/10 bg-surface transition-transform duration-200 lg:static lg:translate-x-0 ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center justify-between px-5">
          <Link
            to="/"
            className="flex items-center gap-2 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
            aria-label="UPLOAD home"
            title="Go to homepage"
          >
            <Mark className="h-6 w-6 text-brand" />
            <Wordmark className="text-lg" />
          </Link>
          <button
            type="button"
            className="icon-btn lg:hidden"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
          >
            <CloseIcon />
          </button>
        </div>
        <button
          type="button"
          onClick={openCommandPalette}
          className="mx-3 mb-3 flex items-center gap-2 rounded-xl border border-line/12 bg-surface-2 px-3 py-2 text-sm text-fg-subtle transition-colors hover:text-fg"
        >
          <SearchIcon className="h-4 w-4" />
          <span className="flex-1 text-left">Search…</span>
          <span className="kbd">⌘K</span>
        </button>
        {nav}
        <div className="border-t border-line/10 p-3">
          <div className="flex items-center gap-3 rounded-xl px-2 py-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand/15 text-sm font-semibold text-brand">
              {(user?.email ?? 'U').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-fg">{user?.email ?? 'Signed in'}</p>
              <p className="text-xs text-fg-subtle">Owner</p>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <ThemeToggle />
            {user ? (
              <button
                type="button"
                className="icon-btn"
                aria-label="Sign out"
                title="Sign out"
                onClick={() => void signOut()}
              >
                <LogoutIcon />
              </button>
            ) : null}
          </div>
        </div>
      </aside>

      {/* Mobile drawer backdrop */}
      {drawerOpen ? (
        <div
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
          aria-hidden="true"
          onClick={() => setDrawerOpen(false)}
        />
      ) : null}

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-line/10 bg-surface-2/80 px-4 backdrop-blur-md sm:px-6">
          <button
            type="button"
            className="icon-btn lg:hidden"
            aria-label="Open menu"
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />
          </button>
          <h1 className="text-base font-semibold text-fg">{title}</h1>
          <button
            type="button"
            onClick={openCommandPalette}
            className="ml-auto inline-flex items-center gap-2 rounded-lg border border-line/12 bg-surface px-3 py-1.5 text-sm text-fg-subtle transition-colors hover:text-fg"
            aria-label="Open command palette"
          >
            <SearchIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Search</span>
            <span className="kbd hidden sm:inline">⌘K</span>
          </button>
        </header>
        <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:px-6 sm:py-8">
          <Outlet />
        </main>
      </div>

      <CommandPalette />
    </div>
  );
}
